import type { SearchRequest, TicketListItem } from '@servicetim/shared';
import { isGroupedSearch, useSearchTickets } from '@servicetim/ui/api';
import { Badge, Button, type ColumnDef, DataTable, ProblemBanner } from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';
import { Play } from 'lucide-react';
import { useMemo, useState } from 'react';

const PREVIEW_SIZE = 20;

/** "Run" preview of a query through `POST /v1/tickets/search` (first page, grouped flattened). */
export function RunPreview({ query }: { query: SearchRequest }) {
  const search = useSearchTickets();
  const [result, setResult] = useState<{
    items: TicketListItem[];
    groups: number | null;
    more: boolean;
  } | null>(null);

  const run = async () => {
    try {
      const body = { ...query, page: { size: Math.min(query.page.size, PREVIEW_SIZE) } };
      const response = await search.mutateAsync(body);
      if (isGroupedSearch(response)) {
        setResult({
          items: response.groups.flatMap((g) => g.items),
          groups: response.groups.length,
          more: response.groups.some((g) => g.nextCursor !== null),
        });
      } else {
        setResult({ items: response.items, groups: null, more: response.nextCursor !== null });
      }
    } catch {
      setResult(null);
    }
  };

  const columns = useMemo<ColumnDef<TicketListItem>[]>(
    () => [
      {
        accessorKey: 'key',
        header: 'Key',
        size: 110,
        cell: ({ row }) => (
          <Link
            to="/tickets/$idOrKey"
            params={{ idOrKey: row.original.key }}
            className="font-mono text-accent hover:underline"
          >
            {row.original.key}
          </Link>
        ),
      },
      { accessorKey: 'summary', header: 'Summary' },
      {
        id: 'status',
        header: 'Status',
        size: 140,
        cell: ({ row }) =>
          row.original.display?.status_id ?? row.original.statusId?.slice(0, 8) ?? '—',
      },
      {
        id: 'priority',
        header: 'Priority',
        size: 120,
        cell: ({ row }) =>
          row.original.display?.priority_id ??
          (row.original.priorityId ? row.original.priorityId.slice(0, 8) : '—'),
      },
      {
        id: 'assignee',
        header: 'Assignee',
        size: 160,
        cell: ({ row }) =>
          row.original.display?.assignee_id ??
          (row.original.assigneeId ? row.original.assigneeId.slice(0, 8) : '—'),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        size: 150,
        cell: ({ getValue }) => {
          const v = getValue<string | undefined>();
          return v ? new Date(v).toLocaleString() : '—';
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => void run()}
          disabled={search.isPending}
        >
          <Play />
          {search.isPending ? 'Running…' : 'Run preview'}
        </Button>
        {result ? (
          <span className="text-fg-muted text-sm">
            {result.items.length} ticket{result.items.length === 1 ? '' : 's'}
            {result.groups !== null
              ? ` in ${result.groups} group${result.groups === 1 ? '' : 's'}`
              : ''}
            {result.more ? ' (more available)' : ''}
          </span>
        ) : null}
        <Badge variant="secondary">first {PREVIEW_SIZE} as you</Badge>
      </div>
      {search.error ? (
        <ProblemBanner error={search.error} size="sm" title="The query was rejected" />
      ) : null}
      {result ? (
        <DataTable
          columns={columns}
          data={result.items}
          getRowId={(t) => t.id}
          dense
          enableSorting={false}
          caption="Preview results"
          emptyTitle="No tickets match"
          emptyDescription="Check the filter, or the tickets you have access to."
        />
      ) : null}
    </div>
  );
}
